import { PlusIcon, Trash03Icon } from '@langchain/untitled-ui-icons';

import { useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { v4 as uuidv4 } from 'uuid';

import { FEEDBACK_STATS_SELECT } from '@/Pages/Project/constants';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import {
  HeaderTitleActionSlot,
  SplitViewPane,
} from '@/components/SplitViewPane';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { Button, IconButton, Input, Text } from '@/design-system';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import { useFeedbackConfigs, useRunStats, useSessions } from '@/hooks/useSwr';
import {
  FeedbackFormulaAggregationType,
  FeedbackFormulaSchema,
  FeedbackFormulaWeightedVariable,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { FeedbackChip } from '../FeedbackChips';
import { CompositeScorePreviewBar } from './CompositeScorePreviewBar';
import { useCompositeScoreCrud } from './useCompositeScoreCrud';

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  error: boolean;
  setError: (error: boolean) => void;
  validate?: (value: number) => boolean;
  min?: string;
  step?: string;
  size?: 'sm' | 'md';
}

function NumberInput({
  value,
  onChange,
  error,
  setError,
  validate,
  min = '0',
  step = '0.1',
  size = 'md',
}: NumberInputProps) {
  const [rawInput, setRawInput] = useState(String(value));

  useEffect(() => {
    setRawInput(String(value));
  }, []);

  function handleChange(v: string) {
    setRawInput(v);
    // Clear error when user types
    if (error) {
      setError(false);
    }
    const numValue = v === '' ? 0 : parseFloat(v);
    if (!isNaN(numValue) && numValue >= 0) {
      onChange(numValue);
    }
  }

  function handleBlur() {
    const numValue = rawInput === '' ? 0 : parseFloat(rawInput);
    if (validate) {
      setError(!validate(numValue));
    }
  }

  return (
    <div className="flex flex-col">
      <Input
        size={size}
        type="number"
        min={min}
        step={step}
        value={rawInput}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(error && 'border-error focus:border-error')}
      />
    </div>
  );
}

interface ScoreRow extends FeedbackFormulaWeightedVariable {
  id: string;
}

interface ScoreTableRowProps {
  row: ScoreRow;
  totalWeight: number;
  method: FeedbackFormulaAggregationType;
  onUpdateRow: (id: string, patch: Partial<ScoreRow>) => void;
  onRemoveRow: (id: string) => void;
}

function ScoreTableRow({
  row,
  totalWeight,
  method,
  onUpdateRow,
  onRemoveRow,
}: ScoreTableRowProps) {
  const [error, setError] = useState(false);
  const percent = totalWeight > 0 ? (row.weight / totalWeight) * 100 : 0;

  function handleWeightChange(newWeight: number) {
    onUpdateRow(row.id, { weight: newWeight });
  }

  function validateWeight(value: number): boolean {
    return value > 0;
  }

  return (
    <TableRow
      className={cn(error && 'border-l-2 border-l-error bg-error-primary')}
    >
      <TableCell className="w-80 px-4">
        <div className="flex items-center gap-2">
          <FeedbackChip feedback_key={row.key} />
          {error && (
            <Text variant="xs" className="mt-1 text-error">
              Weight must be greater than 0
            </Text>
          )}
        </div>
      </TableCell>
      <TableCell className="w-32">
        <NumberInput
          size="md"
          value={row.weight}
          onChange={handleWeightChange}
          error={error}
          setError={setError}
          validate={validateWeight}
        />
      </TableCell>
      <TableCell className="w-20">
        <Text
          className={cn(method === 'sum' && 'invisible')}
          color="tertiary"
          variant="sm"
        >
          {percent.toFixed(1)}%
        </Text>
      </TableCell>
      <TableCell className="w-12">
        <IconButton
          icon={Trash03Icon}
          label="Remove feedback"
          variant="plain"
          color="secondary"
          size="sm"
          onClick={() => onRemoveRow(row.id)}
        />
      </TableCell>
    </TableRow>
  );
}

interface AddFeedbackKeyPopoverProps {
  availableFeedbackKeys: string[];
  selectedKeys: string[];
  onAddScore: (feedbackKey: string) => void;
}

interface ScoreTableProps {
  rows: ScoreRow[];
  totalWeight: number;
  onUpdateRow: (id: string, patch: Partial<ScoreRow>) => void;
  onRemoveRow: (id: string) => void;
  method: FeedbackFormulaAggregationType;
}

function ScoreTable({
  rows,
  totalWeight,
  onUpdateRow,
  onRemoveRow,
  method,
}: ScoreTableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Text variant="h3" className="mb-2 text-tertiary">
          No feedback keys added yet
        </Text>
        <Text variant="sm" className="mb-4 text-center text-tertiary">
          Add your first feedback key to start building your composite score
          formula
        </Text>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-80 px-4">Feedback</TableHead>
          <TableHead className="w-32 px-4">Weight</TableHead>
          <TableHead className="w-20 px-4">
            <Text className={method === 'sum' ? 'invisible' : undefined}>
              %
            </Text>
          </TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <ScoreTableRow
            key={row.id}
            row={row}
            totalWeight={totalWeight}
            method={method}
            onUpdateRow={onUpdateRow}
            onRemoveRow={onRemoveRow}
          />
        ))}
      </TableBody>
    </Table>
  );
}

const OPTION_ELEMENT_HEIGHT = 38;

function AddFeedbackKeyPopover({
  availableFeedbackKeys,
  selectedKeys,
  onAddScore,
}: AddFeedbackKeyPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customFeedbackKey, setCustomFeedbackKey] = useState('');

  function handleAddScore(feedbackKey: string) {
    onAddScore(feedbackKey);
    setIsOpen(false);
    setCustomFeedbackKey('');
  }

  const options = availableFeedbackKeys.filter(
    (key) => !selectedKeys.includes(key)
  );

  const filteredOptions = useMemo(() => {
    if (!customFeedbackKey) return options;
    return options.filter((key) =>
      key.toLowerCase().includes(customFeedbackKey.toLowerCase())
    );
  }, [options, customFeedbackKey]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button leftDecorator={PlusIcon} size="sm" variant="outlined">
          Feedback
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2 py-1">
        <Text variant="sm" className="block py-2 font-medium">
          Select a feedback key
        </Text>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search feedback keys..."
            value={customFeedbackKey}
            onValueChange={setCustomFeedbackKey}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandGroup className="mx-0">
              <Virtuoso
                style={{
                  height: Math.min(
                    filteredOptions.length * OPTION_ELEMENT_HEIGHT,
                    OPTION_ELEMENT_HEIGHT * 6
                  ),
                }}
                data={filteredOptions}
                itemContent={(_, feedbackKey) => (
                  <CommandItem
                    onSelect={handleAddScore}
                    style={{ height: OPTION_ELEMENT_HEIGHT }}
                  >
                    <FeedbackChip feedback_key={feedbackKey} />
                  </CommandItem>
                )}
              />
            </CommandGroup>
            {customFeedbackKey &&
              !availableFeedbackKeys.includes(customFeedbackKey) && (
                <CommandGroup className="mx-0">
                  <CommandItem
                    onSelect={handleAddScore}
                    className={
                      selectedKeys.includes(customFeedbackKey)
                        ? 'text-tertiary'
                        : 'text-brand'
                    }
                    disabled={selectedKeys.includes(customFeedbackKey)}
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <PlusIcon className="h-4 w-4" />
                      <FeedbackChip feedback_key={customFeedbackKey} />
                    </div>
                  </CommandItem>
                </CommandGroup>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface ScorePreviewProps {
  rows: ScoreRow[];
  totalWeight: number;
  method: FeedbackFormulaAggregationType;
  name: string;
}

function ScorePreview({ rows, totalWeight, method, name }: ScorePreviewProps) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="mt-auto rounded-md border border-secondary bg-secondary p-4">
      <div className="mb-2 flex items-center justify-between">
        <Text variant="xs" className="uppercase tracking-wide text-tertiary">
          Preview – {method === 'sum' ? 'Sum' : 'Average'}
        </Text>
      </div>
      <div className="flex flex-col gap-2">
        <Text variant="md" weight="medium">
          {name || 'Overall response quality'}
        </Text>
        <CompositeScorePreviewBar
          rows={rows}
          totalWeight={totalWeight}
          variant="score-pane"
          method={method}
        />
      </div>
    </section>
  );
}

// Common interface for the base component
interface CompositeScorePaneContentBaseProps {
  availableFeedbackKeys: string[];
  initialFormula?: FeedbackFormulaSchema;
  onSaved?: (formula: FeedbackFormulaSchema) => void;
  onClose: () => void;
  onSave: (data: {
    name: string;
    method: FeedbackFormulaAggregationType;
    rows: ScoreRow[];
  }) => Promise<void>;
  isSaving: boolean;
  error: string | null;
  contextId: string; // datasetId or sessionId for validation
}

// Common component that handles the UI logic
function CompositeScorePaneContentBase({
  availableFeedbackKeys,
  initialFormula,
  onSave,
  isSaving,
  error,
  contextId,
}: CompositeScorePaneContentBaseProps) {
  const [name, setName] = useState('');
  const [method, setMethod] = useState<FeedbackFormulaAggregationType>('avg');
  const [rows, setRows] = useState<ScoreRow[]>([]);

  const totalWeight = useMemo(
    () =>
      rows.reduce(
        (acc, r) => acc + (Number.isFinite(r.weight) ? r.weight : 0),
        0
      ),
    [rows]
  );

  function updateRow(id: string, patch: Partial<ScoreRow>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function addRow(feedbackKey?: string) {
    setRows((prev) => [
      ...prev,
      {
        id: uuidv4(),
        key: feedbackKey || '',
        weight: 1,
        part_type: 'weighted_key' as const,
      },
    ]);
  }

  function handleAddScore(feedbackKey: string) {
    addRow(feedbackKey);
  }

  function removeRow(id: string) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  // hydrate from existing formula
  useEffect(() => {
    if (!initialFormula) return;
    setName(initialFormula.feedback_key);
    setMethod(initialFormula.aggregation_type);
    if (initialFormula.formula_parts?.length) {
      setRows(
        initialFormula.formula_parts.map((p) => ({
          id: uuidv4(),
          key: p.key,
          weight: p.weight,
          part_type: p.part_type,
        }))
      );
    }
  }, [initialFormula]);

  async function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    await onSave({
      name: trimmedName,
      method,
      rows,
    });
  }

  return (
    <div className="flex h-full flex-col">
      {error && (
        <div className="p-4">
          <ExpandableErrorAlert error={error} />
        </div>
      )}

      <HeaderTitleActionSlot.Fill>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSave}
            size="md"
            disabled={!name.trim() || (!initialFormula && !contextId)}
            loading={isSaving}
          >
            {initialFormula ? 'Save' : 'Create'}
          </Button>
        </div>
      </HeaderTitleActionSlot.Fill>

      <div className="flex flex-1 flex-col gap-6 p-4">
        <section className="flex flex-col gap-3">
          <Text variant="h3">Name</Text>
          <Input
            placeholder="Overall response quality"
            value={name}
            onChange={setName}
            autoFocus
          />
        </section>

        <section className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Text variant="h3">Aggregation method</Text>{' '}
              <GroupedTabs<FeedbackFormulaAggregationType>
                value={method}
                onChange={(v) => setMethod(v)}
                options={[
                  {
                    value: 'avg',
                    display: 'Average',
                    tooltip: <code>sum(weight*score) / sum(weight)</code>,
                  },
                  {
                    value: 'sum',
                    display: 'Sum',
                    tooltip: <code>sum(weight*score)</code>,
                  },
                ]}
              />
            </div>
            <AddFeedbackKeyPopover
              availableFeedbackKeys={availableFeedbackKeys}
              selectedKeys={rows.map((row) => row.key)}
              onAddScore={handleAddScore}
            />
          </div>
          <ScorePreview
            rows={rows}
            totalWeight={totalWeight}
            method={method}
            name={name}
          />

          <div className="rounded-md border border-secondary">
            <ScoreTable
              rows={rows}
              totalWeight={totalWeight}
              onUpdateRow={updateRow}
              onRemoveRow={removeRow}
              method={method}
            />
          </div>

          {method === 'avg' && (
            <Text variant="xs" className="text-tertiary">
              We normalize weights to compute percentages. Keep relative ratios;
              exact values are not required.
            </Text>
          )}
        </section>
      </div>
    </div>
  );
}

interface CompositeScorePaneContentWrapperProps {
  initialFormula?: FeedbackFormulaSchema;
  onSaved?: (formula: FeedbackFormulaSchema) => void;
  onClose: () => void;
}

interface DatasetCompositeScorePaneContentProps
  extends CompositeScorePaneContentWrapperProps {
  datasetId: string;
}

function DatasetCompositeScorePaneContent({
  datasetId,
  initialFormula,
  onSaved,
  onClose,
}: DatasetCompositeScorePaneContentProps) {
  const { handleSave, isMutating, error } = useCompositeScoreCrud({
    datasetId,
    initialFormula,
    onSaved,
    onClose,
  });

  // Fetch recent sessions to get available feedback keys
  const { data: sessionsData } = useSessions({
    limit: 20,
    offset: 0,
    include_stats: true,
    reference_dataset: [datasetId],
  });

  // Extract unique feedback keys from sessions
  const availableFeedbackKeys = useMemo(() => {
    const feedbackKeys = new Set<string>();

    sessionsData?.rows?.forEach((session) => {
      // Get regular feedback keys
      if (session.feedback_stats) {
        Object.entries(session.feedback_stats)
          .filter(([, value]) => value.type !== 'composite')
          .forEach(([key]) => {
            feedbackKeys.add(key);
          });
      }

      // Get session feedback keys
      Object.keys(session.session_feedback_stats ?? {}).forEach((key) => {
        feedbackKeys.add(key);
      });
    });
    return Array.from(feedbackKeys).sort();
  }, [sessionsData?.rows]);

  const { data: feedbackConfigs } = useFeedbackConfigs(
    {},
    {
      key: availableFeedbackKeys,
    }
  );

  const continuousKeys = useMemo(() => {
    return (
      feedbackConfigs
        ?.filter((config) => {
          return config.feedback_config.type === 'continuous';
        })
        .map((config) => config.feedback_key) ?? []
    );
  }, [feedbackConfigs]);

  return (
    <CompositeScorePaneContentBase
      availableFeedbackKeys={continuousKeys}
      initialFormula={initialFormula}
      onSaved={onSaved}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isMutating}
      error={error?.message ?? null}
      contextId={datasetId}
    />
  );
}
interface SessionCompositeScorePaneContentProps
  extends CompositeScorePaneContentWrapperProps {
  sessionId: string;
}

function SessionCompositeScorePaneContent({
  sessionId,
  initialFormula,
  onSaved,
  onClose,
}: SessionCompositeScorePaneContentProps) {
  // Fetch recent sessions to get available feedback keys
  const { data: stats } = useRunStats({
    session: [sessionId],
    select: FEEDBACK_STATS_SELECT,
  });
  const sessionFeedbackKeys = Object.keys(stats?.feedback_stats ?? {});

  const { data: feedbackConfigs } = useFeedbackConfigs(
    {},
    {
      key: sessionFeedbackKeys,
    }
  );

  const continuousKeys = useMemo(() => {
    return (
      feedbackConfigs
        ?.filter((config) => {
          return config.feedback_config.type === 'continuous';
        })
        .map((config) => config.feedback_key) ?? []
    );
  }, [feedbackConfigs]);

  const { handleSave, isMutating, error } = useCompositeScoreCrud({
    sessionId,
    initialFormula,
    onSaved,
    onClose,
  });

  return (
    <CompositeScorePaneContentBase
      availableFeedbackKeys={continuousKeys}
      initialFormula={initialFormula}
      onSaved={onSaved}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isMutating}
      error={error?.message ?? null}
      contextId={sessionId}
    />
  );
}

export type CompositeScorePaneProps = (
  | {
      datasetId: string;
      sessionId?: never;
      // other props...
    }
  | {
      datasetId?: never;
      sessionId: string;
      // other props...
    }
) & {
  open: boolean;
  onClose: () => void;
  defaultWidth?: number;

  initialFormula?: FeedbackFormulaSchema;
  onSaved?: (formula: FeedbackFormulaSchema) => void;
};

export function CompositeScorePane({
  open,
  onClose,
  defaultWidth = 680,
  datasetId,
  sessionId,
  initialFormula,
  onSaved,
}: CompositeScorePaneProps) {
  return (
    <SplitViewPane
      title={initialFormula ? 'Edit Composite Score' : 'Create Composite Score'}
      hideArrows
      open={open}
      onClose={onClose}
      sidePaneId="composite-score-pane"
      defaultWidth={defaultWidth}
    >
      {datasetId ? (
        <DatasetCompositeScorePaneContent
          datasetId={datasetId}
          initialFormula={initialFormula}
          onSaved={onSaved}
          onClose={onClose}
        />
      ) : sessionId ? (
        <SessionCompositeScorePaneContent
          sessionId={sessionId}
          initialFormula={initialFormula}
          onSaved={onSaved}
          onClose={onClose}
        />
      ) : null}
    </SplitViewPane>
  );
}

export default CompositeScorePane;
